import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import * as Excel from 'exceljs';
import { saveAs } from 'file-saver';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { NO_LABEL } from '@connect-services/client/services/chart.service';
import { SecurityService } from '@connect-services/client/services/security.service';
import { TranslateService } from '@connect-services/client/services/translate.service';
import {
    ComputedStatistic,
    DepositDataVersion,
    LineColumnComputedStatisticResult,
    PieComputedStatisticResult,
    SimpleValueData,
} from '@connect-services/src/sdk/api';

const DEFAULT_COLUMN_WIDTH = 24;
const EXCEL_SHEET_NAME_MAX_LENGTH = 31;
const EXCEL_INVALID_SHEET_NAME_REGEX = /[:\\/?*[\]]/g;
const INFORMATION_LABEL_COLUMN_WIDTH = 28;
const INFORMATION_VALUE_COLUMN_WIDTH = 44;
const INFORMATION_EMPTY_ROW_NUMBER = 5;
const INFORMATION_LAST_BOLD_ROW_NUMBER = 6;

@Injectable({
    providedIn: 'root',
})
export class ExportStatisticsExcelService {
    private readonly projectBloc = inject(ProjectBloc);
    private readonly securityService = inject(SecurityService);
    private readonly translateService = inject(TranslateService);
    private readonly translocoService = inject(TranslocoService);

    public async exportStatistics(
        statistics: ComputedStatistic[],
        standardName: string,
        dataVersions: DepositDataVersion[],
    ): Promise<void> {
        const workbook = new Excel.Workbook();

        workbook.creator = 'ConnectServices';
        workbook.created = new Date();

        this.addCoverWorksheet(workbook, standardName, dataVersions);
        this.addStatisticWorksheets(workbook, statistics);

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        saveAs(blob, this.buildFileName());
    }

    private addCoverWorksheet(
        workbook: Excel.Workbook,
        standardName: string,
        dataVersions: DepositDataVersion[],
    ): void {
        const worksheet = workbook.addWorksheet(this.translocoService.translate('generalInformation'));
        const user = this.securityService.meSignal();
        const projectName = this.projectBloc.value()?.name ?? '';
        const exportDate = new Date().toLocaleString(
            this.translateService.getLocaleFromLang(this.translateService.getCurrentLang()),
        );

        worksheet.getColumn(1).width = INFORMATION_LABEL_COLUMN_WIDTH;
        worksheet.getColumn(2).width = INFORMATION_VALUE_COLUMN_WIDTH;

        worksheet.addRow([this.translocoService.translate('projectName'), projectName]);
        worksheet.addRow([this.translocoService.translate('standard'), standardName]);
        worksheet.addRow([this.translocoService.translate('date'), exportDate]);
        worksheet.addRow([this.translocoService.translate('exportedByUser'), user?.fullName ?? user?.email ?? '']);
        worksheet.addRow([]);
        worksheet.addRow([this.translocoService.translate('dataUsedList')]);

        const headerRow = worksheet.addRow([
            this.translocoService.translate('dataName'),
            this.translocoService.translate('version'),
        ]);
        headerRow.font = { bold: true };

        dataVersions.forEach((dataVersion: DepositDataVersion) => {
            worksheet.addRow([dataVersion.dataName, dataVersion.version]);
        });

        for (let rowNumber = 1; rowNumber <= INFORMATION_LAST_BOLD_ROW_NUMBER; rowNumber++) {
            if (rowNumber === INFORMATION_EMPTY_ROW_NUMBER) {
                continue;
            }

            worksheet.getCell(`A${rowNumber}`).font = { bold: true };
        }
    }

    private addStatisticWorksheets(workbook: Excel.Workbook, statistics: ComputedStatistic[]): void {
        const usedSheetNames = new Set<string>();

        statistics.forEach((statistic: ComputedStatistic) => {
            const worksheet = workbook.addWorksheet(this.buildWorksheetName(statistic.name, usedSheetNames));
            const headers = this.getHeaders(statistic);
            const rows = this.getRows(statistic);

            worksheet.addRow([statistic.name]);
            worksheet.mergeCells(1, 1, 1, Math.max(headers.length, 1));
            worksheet.getRow(1).font = { bold: true, size: 14 };

            worksheet.addRow([]);

            const headerRow = worksheet.addRow(headers);
            headerRow.font = { bold: true };

            rows.forEach((row: Array<string | number | null>) => worksheet.addRow(row));

            headers.forEach((header: string, index: number) => {
                worksheet.getColumn(index + 1).width = this.getColumnWidth(header, rows, index);
            });
        });
    }

    private buildFileName(): string {
        const date = new Date().toISOString().slice(0, 10);
        const projectName = this.sanitizeFilePart(this.projectBloc.value()?.name ?? 'projet');

        return `${date}_${projectName}_${this.sanitizeFilePart(this.translocoService.translate('statistics'))}.xlsx`;
    }

    private buildWorksheetName(statisticName: string, usedSheetNames: Set<string>): string {
        const sanitizedBaseName =
            statisticName.replace(EXCEL_INVALID_SHEET_NAME_REGEX, '').trim().slice(0, EXCEL_SHEET_NAME_MAX_LENGTH) ||
            this.translocoService.translate('statistics');

        let sheetName = sanitizedBaseName;
        let suffix = 1;

        while (usedSheetNames.has(sheetName)) {
            const suffixText = `_${suffix}`;
            const maxBaseLength = EXCEL_SHEET_NAME_MAX_LENGTH - suffixText.length;

            sheetName = `${sanitizedBaseName.slice(0, maxBaseLength)}${suffixText}`;
            suffix += 1;
        }

        usedSheetNames.add(sheetName);

        return sheetName;
    }

    private getColumnWidth(header: string, rows: Array<Array<string | number | null>>, index: number): number {
        const maxContentWidth = rows.reduce((maxWidth, row) => {
            const value = row[index];

            return Math.max(maxWidth, String(value ?? '').length);
        }, header.length);

        return Math.max(DEFAULT_COLUMN_WIDTH, Math.min(maxContentWidth + 2, 50));
    }

    private getHeaders(statistic: ComputedStatistic): string[] {
        if (
            [
                ComputedStatistic.RenderEnum.VALUE,
                ComputedStatistic.RenderEnum.GAUGE,
                ComputedStatistic.RenderEnum.HALF_GAUGE,
            ].includes(statistic.render)
        ) {
            return [this.translocoService.translate('value')];
        }

        if (statistic.render === ComputedStatistic.RenderEnum.PIE) {
            return [this.translocoService.translate('label'), this.translocoService.translate('value')];
        }

        const xAxisTitle = statistic.renderOptions.xaxisTitle?.text || 'x';
        const yAxisTitle = statistic.renderOptions.yaxisTitle?.text || 'y';

        if (this.hasMultipleSeriesLabels(statistic)) {
            return [xAxisTitle, yAxisTitle, this.translocoService.translate('label')];
        }

        return [xAxisTitle, yAxisTitle];
    }

    private getRows(statistic: ComputedStatistic): Array<Array<string | number | null>> {
        if (
            [
                ComputedStatistic.RenderEnum.VALUE,
                ComputedStatistic.RenderEnum.GAUGE,
                ComputedStatistic.RenderEnum.HALF_GAUGE,
            ].includes(statistic.render)
        ) {
            const result = (statistic.result?.data ?? {}) as SimpleValueData;

            return [[result.x ?? null]];
        }

        if (statistic.render === ComputedStatistic.RenderEnum.PIE) {
            const result = (statistic.result?.data ?? []) as PieComputedStatisticResult[];

            return result.map((item: PieComputedStatisticResult) => [item.label ?? '', item.x ?? null]);
        }

        const result = (statistic.result?.data ?? []) as LineColumnComputedStatisticResult[];

        if (this.hasMultipleSeriesLabels(statistic)) {
            return result.map((item: LineColumnComputedStatisticResult) => [
                item.x ?? '',
                item.y ?? null,
                item.label ?? '',
            ]);
        }

        return result.map((item: LineColumnComputedStatisticResult) => [item.x ?? '', item.y ?? null]);
    }

    private hasMultipleSeriesLabels(statistic: ComputedStatistic): boolean {
        const result = (statistic.result?.data ?? []) as LineColumnComputedStatisticResult[];
        const labels = new Set(
            result
                .map((item: LineColumnComputedStatisticResult) => item.label)
                .filter((label): label is string => !!label && label !== NO_LABEL),
        );

        return labels.size > 1;
    }

    private sanitizeFilePart(value: string): string {
        return value.replace(/[<>:"/\\|?*]/g, '').trim() || 'projet';
    }
}
